import React, { useState } from 'react';
import { rideService } from '../../../../services/rideService';
import Icon from '../../../../components/AppIcon';


const CreateRideModal = ({ eventId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    originLocation: '',
    departureTime: '',
    availableSeats: 3,
    pricePerSeat: 0,
    carDetails: '',
    notes: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'availableSeats' || name === 'pricePerSeat' ? Number(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Validation
    if (!formData?.originLocation?.trim()) {
      setError('Başlangıç noktası zorunludur');
      return;
    }

    if (!formData?.departureTime) {
      setError('Kalkış saati zorunludur');
      return;
    }

    if (formData?.availableSeats < 1 || formData?.availableSeats > 8) {
      setError('Koltuk sayısı 1-8 arasında olmalıdır');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await rideService?.createRide({
        eventId,
        originLocation: formData?.originLocation,
        departureTime: formData?.departureTime,
        availableSeats: formData?.availableSeats,
        pricePerSeat: formData?.pricePerSeat,
        carDetails: formData?.carDetails || null,
        notes: formData?.notes || null
      });

      onSuccess?.();
    } catch (err) {
      console.error('Error creating ride:', err);
      setError(err?.message || 'İlan oluşturulurken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-foreground">Araç İlanı Oluştur</h3>
            <p className="text-sm text-muted-foreground mt-1">Etkinliğe birlikte gidebileceğiniz kişileri bulun</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Hata</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Origin Location */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Başlangıç Noktası <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Icon name="MapPin" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                name="originLocation"
                value={formData?.originLocation}
                onChange={handleChange}
                placeholder="Örn: Kadıköy İskele"
                required
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Departure Time */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Kalkış Saati <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Icon name="Clock" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="datetime-local"
                name="departureTime"
                value={formData?.departureTime}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Available Seats & Price */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Koltuk Sayısı <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Icon name="Users" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="number"
                  name="availableSeats"
                  value={formData?.availableSeats}
                  onChange={handleChange}
                  min="1"
                  max="8"
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Kişi Başı Ücret (TL)
              </label>
              <div className="relative">
                <Icon name="Wallet" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="number"
                  name="pricePerSeat"
                  value={formData?.pricePerSeat}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">0 ise ücretsiz</p>
            </div>
          </div>

          {/* Car Details */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Araç Bilgisi
            </label>
            <div className="relative">
              <Icon name="Car" size={18} className="absolute left-3 top-3 text-muted-foreground" />
              <input
                type="text"
                name="carDetails"
                value={formData?.carDetails}
                onChange={handleChange}
                placeholder="Örn: Beyaz Renault Clio, 2020 model"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notlar
            </label>
            <div className="relative">
              <Icon name="MessageSquare" size={18} className="absolute left-3 top-3 text-muted-foreground" />
              <textarea
                name="notes"
                value={formData?.notes}
                onChange={handleChange}
                placeholder="Örn: Kadıköy'den geçeceğim, yolda alırım"
                rows={3}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
              />
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <Icon name="Info" size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Bilgilendirme</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>İlanınız oluşturulduktan sonra diğer kullanıcılar katılabilir</li>
                <li>İstediğiniz zaman ilanınızı iptal edebilirsiniz</li>
                <li>Güvenliğiniz için tanımadığınız kişilerle dikkatli olun</li>
              </ul>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 py-2.5 border border-gray-300 text-foreground rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  <span>Oluşturuluyor...</span>
                </>
              ) : (
                <>
                  <Icon name="Check" size={18} />
                  <span>İlanı Oluştur</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRideModal;